import {ChangeDetectionStrategy, Component, inject, Injector, Input, OnInit} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TUI_EDITOR_DEFAULT_EXTENSIONS, TUI_EDITOR_EXTENSIONS, TuiEditor} from '@taiga-ui/editor';
import {BaseComponent, HtmlValidatorContent, TemplateFeature} from 'compob2b-libs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {
  TemplateFeatureService,
  TemplateFeatureValue
} from '../../../../../../compob2b-admin/src/app/cabinet/pim/product/create-update/features/template-feature.service'
import {NgIf} from '@angular/common';
import {NbIconModule, NbTooltipModule} from '@nebular/theme';
import {RouterLink} from '@angular/router';

@Component({
  selector: 'app-tf-html',
  templateUrl: './tf-html.component.html',
  providers: [
    {
      provide: TUI_EDITOR_EXTENSIONS,
      deps: [Injector],
      useFactory: (injector: Injector) => [
        ...TUI_EDITOR_DEFAULT_EXTENSIONS,
        import('@taiga-ui/editor').then(({setup}) => setup({injector})),
      ],
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NbTooltipModule, NbIconModule, FormsModule, ReactiveFormsModule, TuiEditor, RouterLink]
})
export class TfHtmlComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;

  public readonly maxHtmlContentLength = this.environment.maxHtmlContentLength;

  form = new UntypedFormGroup({
    featureValue: new UntypedFormControl(null)
  });

  isValidatorSet = false;
  validatorName: string = null;
  htmlValidator: HtmlValidatorContent = null;

  templateFeatureService = inject(TemplateFeatureService);

  ngOnInit(): void {
    this.setValidator();

    this.form.get('featureValue').valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        this.setFeature(data !== null && data.trim() !== '' ? data.trim() : null, this.form.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setValidator(): void {
    if (this.feature.validator !== null) {
      this.isValidatorSet = true;
      this.validatorName = this.feature.validator.header;
      this.htmlValidator = this.feature.validator.content.htmlValidator;

      const validators = [];
      if (this.htmlValidator.minLength !== null) {
        validators.push(Validators.minLength(this.htmlValidator.minLength));
      }

      if (this.htmlValidator.maxLength !== null) {
        validators.push(Validators.maxLength(this.htmlValidator.maxLength));
      }

      if (validators.length > 0) {
        this.featureValue.addValidators(validators);
      }
    }
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      this.featureValue.setValue(featureModel[this.feature.id], {emitEvent: false});
    }

    const value = this.featureValue.value !== null && this.featureValue.value.trim() !== '' ? this.featureValue.value.trim() : null;
    this.setDefaultFeatureValue(value);
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }

  isEmpty(): boolean {
    return !this.featureValue.value;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  get featureValue(): any {
    return this.form.get('featureValue');
  }
}
